/**
 * Mock Service Infrastructure
 * 
 * Provides base classes and utilities for creating mock services
 * that can simulate API responses for testing without external dependencies.
 * 
 * Usage:
 * ```javascript
 * import { MockServiceBase, createMockResponse, simulateLatency } from 'services/mocks';
 * ```
 */

// ============================================================================
// DEV MODE DETECTION
// ============================================================================

/**
 * Check if the application is running in dev/test mode
 * @returns {boolean}
 */
export const isDevMode = () => {
  if (typeof window === 'undefined') return false;
  
  // Check for dev mode user in localStorage
  const devModeUser = localStorage.getItem('devModeUser');
  if (devModeUser) return true;
  
  // Check environment variable
  if (process.env.REACT_APP_TEST_MODE === 'true') return true;
  
  // Check URL parameter
  const params = new URLSearchParams(window.location.search);
  if (params.get('testMode') === 'true') return true;
  
  // Check localhost
  return (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.startsWith('192.168.')
  );
};

/**
 * Check if mock services should be used
 * Respects explicit mock disable
 * @returns {boolean}
 */
export const shouldUseMocks = () => {
  if (typeof window === 'undefined') return false;
  
  // Check for explicit mock disable
  const params = new URLSearchParams(window.location.search);
  if (params.get('useMocks') === 'false') return false;
  
  // Check localStorage override
  if (localStorage.getItem('disableMocks') === 'true') return false;
  
  return isDevMode();
};

// ============================================================================
// LATENCY SIMULATION
// ============================================================================

/**
 * Configuration for latency simulation
 */
export const LATENCY_CONFIG = {
  // Base latencies in milliseconds
  fast: { min: 50, max: 150 },
  normal: { min: 200, max: 500 },
  slow: { min: 800, max: 1500 },
  llm: { min: 1000, max: 3000 },
  
  // Current mode (can be changed at runtime)
  currentMode: 'normal',
};

/**
 * Set the latency simulation mode
 * @param {'fast' | 'normal' | 'slow' | 'llm' | 'none'} mode
 */
export const setLatencyMode = (mode) => {
  LATENCY_CONFIG.currentMode = mode;
};

/**
 * Simulate network latency
 * @param {string} mode - Latency mode or use current default
 * @returns {Promise<void>}
 */
export const simulateLatency = async (mode = LATENCY_CONFIG.currentMode) => {
  if (mode === 'none') return;
  
  const config = LATENCY_CONFIG[mode] || LATENCY_CONFIG.normal;
  const delay = Math.random() * (config.max - config.min) + config.min;
  
  await new Promise(resolve => setTimeout(resolve, delay));
};

// ============================================================================
// MOCK RESPONSE GENERATORS
// ============================================================================

/**
 * Create a successful mock response
 * @param {any} data - Response data
 * @param {string} message - Optional message
 * @returns {Object}
 */
export const createMockResponse = (data, message = 'Success') => ({
  success: true,
  data,
  message,
  timestamp: new Date().toISOString(),
  isMock: true,
});

/**
 * Create a mock error response
 * @param {string} message - Error message
 * @param {string} code - Error code
 * @returns {Object}
 */
export const createMockError = (message, code = 'MOCK_ERROR') => ({
  success: false,
  error: { message, code },
  timestamp: new Date().toISOString(),
  isMock: true,
});

/**
 * Randomly decide if a mock should fail (for testing error handling)
 * @param {number} failureRate - Probability of failure (0-1)
 * @returns {boolean}
 */
export const shouldSimulateError = (failureRate = 0) => {
  return Math.random() < failureRate;
};

// ============================================================================
// BASE MOCK SERVICE CLASS
// ============================================================================

/**
 * Base class for mock services
 * Provides common functionality for all mock services
 */
export class MockServiceBase {
  constructor(serviceName) {
    this.serviceName = serviceName;
    this.callLog = [];
    this.latencyMode = 'normal';
    this.errorRate = 0;
    this.enabled = true;
  }
  
  /**
   * Log a service call for debugging/testing
   * @param {string} method - Method name
   * @param {any} params - Call parameters
   * @param {any} result - Call result
   */
  logCall(method, params, result) {
    this.callLog.push({
      method,
      params,
      result,
      timestamp: new Date().toISOString(),
    });
    
    // Keep only last 100 calls
    if (this.callLog.length > 100) {
      this.callLog = this.callLog.slice(-100);
    }
    
    // Log to console in dev mode
    if (isDevMode()) {
    }
  }
  
  /**
   * Get the call log for testing
   * @returns {Array}
   */
  getCallLog() {
    return [...this.callLog];
  }
  
  /**
   * Clear the call log
   */
  clearCallLog() {
    this.callLog = [];
  }
  
  /**
   * Set the latency mode for this service
   * @param {string} mode
   */
  setLatencyMode(mode) {
    this.latencyMode = mode;
  }
  
  /**
   * Set the error rate for this service
   * @param {number} rate - 0-1
   */
  setErrorRate(rate) {
    this.errorRate = rate;
  }
  
  /**
   * Enable or disable the mock service
   * @param {boolean} enabled
   */
  setEnabled(enabled) {
    this.enabled = enabled;
  }
  
  /**
   * Wrap a mock method with common functionality
   * @param {string} methodName
   * @param {Function} handler
   * @returns {Function}
   */
  wrapMethod(methodName, handler) {
    return async (...args) => {
      if (!this.enabled) {
        throw new Error(`Mock service ${this.serviceName} is disabled`);
      }
      
      await simulateLatency(this.latencyMode);
      
      if (shouldSimulateError(this.errorRate)) {
        const error = createMockError(
          `Simulated error in ${methodName}`,
          'SIMULATED_ERROR'
        );
        this.logCall(methodName, args, error);
        throw error;
      }
      
      const result = await handler(...args);
      this.logCall(methodName, args, result);
      return result;
    };
  }
}

// ============================================================================
// MOCK DATA STORE
// ============================================================================

/**
 * In-memory data store for mock services
 * Simulates a simple database
 */
export class MockDataStore {
  constructor(initialData = {}) {
    this.data = { ...initialData };
    this.changeListeners = [];
  }
  
  /**
   * Get all items in a collection
   * @param {string} collection
   * @returns {Array}
   */
  getAll(collection) {
    return Object.values(this.data[collection] || {});
  }
  
  /**
   * Get a single item by ID
   * @param {string} collection
   * @param {string} id
   * @returns {Object|null}
   */
  get(collection, id) {
    if (!this.data[collection]) return null;
    return this.data[collection][id] || null;
  }
  
  /**
   * Add or update an item
   * @param {string} collection
   * @param {string} id
   * @param {Object} item
   * @returns {Object}
   */
  set(collection, id, item) {
    if (!this.data[collection]) {
      this.data[collection] = {};
    }
    this.data[collection][id] = { ...item, id };
    this.notifyListeners(collection, 'set', id);
    return this.data[collection][id];
  }
  
  /**
   * Delete an item
   * @param {string} collection
   * @param {string} id
   * @returns {boolean}
   */
  delete(collection, id) {
    if (!this.data[collection] || !this.data[collection][id]) {
      return false;
    }
    delete this.data[collection][id];
    this.notifyListeners(collection, 'delete', id);
    return true;
  }
  
  /**
   * Query items with a filter function
   * @param {string} collection
   * @param {Function} filterFn
   * @returns {Array}
   */
  query(collection, filterFn) {
    return this.getAll(collection).filter(filterFn);
  }
  
  /**
   * Add a change listener
   * @param {Function} listener
   * @returns {Function} Unsubscribe function
   */
  onChange(listener) {
    this.changeListeners.push(listener);
    return () => {
      this.changeListeners = this.changeListeners.filter(l => l !== listener);
    };
  }
  
  /**
   * Notify listeners of changes
   * @param {string} collection
   * @param {string} action
   * @param {string} id
   */
  notifyListeners(collection, action, id) {
    this.changeListeners.forEach(listener => {
      listener({ collection, action, id });
    });
  }
  
  /**
   * Reset the store to initial state
   * @param {Object} initialData
   */
  reset(initialData = {}) {
    this.data = { ...initialData };
    this.notifyListeners('*', 'reset', null);
  }
  
  /**
   * Export store data for debugging
   * @returns {Object}
   */
  export() {
    return JSON.parse(JSON.stringify(this.data));
  }
  
  /**
   * Import data into store
   * @param {Object} data
   */
  import(data) {
    this.data = JSON.parse(JSON.stringify(data));
    this.notifyListeners('*', 'import', null);
  }
}

// ============================================================================
// GLOBAL MOCK STORE INSTANCE
// ============================================================================

export const globalMockStore = new MockDataStore();

// ============================================================================
// TEST SCENARIO MANAGEMENT
// ============================================================================

/**
 * Registry of test scenarios
 */
const testScenarios = {};

/**
 * Register a test scenario
 * @param {string} name - Scenario name
 * @param {Function} setup - Setup function that configures mocks
 */
export const registerScenario = (name, setup) => {
  testScenarios[name] = setup;
};

/**
 * Load a test scenario
 * @param {string} name - Scenario name
 * @returns {boolean} Success
 */
export const loadScenario = (name) => {
  if (!testScenarios[name]) {
    console.error(`Unknown test scenario: ${name}`);
    return false;
  }
  
  testScenarios[name](globalMockStore);
  return true;
};

/**
 * List available test scenarios
 * @returns {string[]}
 */
export const listScenarios = () => Object.keys(testScenarios);

// ============================================================================
// BROWSER CONSOLE API
// ============================================================================

// Expose utilities to browser console for testing
if (typeof window !== 'undefined') {
  window.__MOCKS__ = {
    isDevMode,
    shouldUseMocks,
    setLatencyMode,
    globalStore: globalMockStore,
    loadScenario,
    listScenarios,
    LATENCY_CONFIG,
  };
}

const mockServiceExports = {
  isDevMode,
  shouldUseMocks,
  simulateLatency,
  setLatencyMode,
  createMockResponse,
  createMockError,
  shouldSimulateError,
  MockServiceBase,
  MockDataStore,
  globalMockStore,
  registerScenario,
  loadScenario,
  listScenarios,
};

export default mockServiceExports;

